import React, { useState, useEffect, useMemo } from 'react';
import './matchgame.css';
import trueSound from './assets/true.wav';
import wrongSound from './assets/wrong.wav';

function MatchGame() {
  // กำหนดคู่ภาพจาก local assets (memoize เพื่อไม่ให้สร้างใหม่ทุก render)
  const imagePairs = useMemo(() => Array.from({ length: 10 }, (_, index) => ({
    pairId: index + 1,
    image1: require(`./assets/hand-${index}.png`),
    image2: require(`./assets/number-${index}.png`)
  })), []);

  // สร้างการ์ดจากคู่ภาพ
  const createCards = () => {
    let gameCards = [];
    
    imagePairs.forEach(pair => {
      gameCards.push({
        id: `${pair.pairId}-1`,
        pairId: pair.pairId,
        cardType: 'image1',
        imageUrl: pair.image1,
        flipped: false,
        matched: false
      });
      
      gameCards.push({
        id: `${pair.pairId}-2`,
        pairId: pair.pairId,
        cardType: 'image2',
        imageUrl: pair.image2,
        flipped: false,
        matched: false
      });
    });
    
    // สุ่มลำดับการ์ด
    return shuffleArray(gameCards);
  };

  // สุ่มลำดับอาร์เรย์
  const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  // State สำหรับเกม
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState(0);
  const [moves, setMoves] = useState(0);
  const [canFlip, setCanFlip] = useState(true);
  const [gameWon, setGameWon] = useState(false);

  // เริ่มเกมใหม่
  const startGame = () => {
    const newCards = createCards();
    setCards(newCards);
    setFlippedCards([]);
    setMatchedPairs(0);
    setMoves(0);
    setCanFlip(true);
    setGameWon(false);
  };

  // เริ่มเกมครั้งแรกเมื่อโหลด
  useEffect(() => {
    startGame();
  }, []);

  // ตรวจสอบเมื่อการ์ดถูกพลิก
  useEffect(() => {
    if (flippedCards.length === 2) {
      setCanFlip(false);
      // ✅ ใช้ functional update เพื่อหลีกเลี่ยง stale closure
      setMoves(prev => prev + 1);
      
      // ตรวจสอบว่าการ์ดเป็นคู่ที่ถูกต้องหรือไม่
      const [card1, card2] = flippedCards;
      const isMatch = card1.pairId === card2.pairId;
      
      // ✅ เล่นเสียงตามผลลัพธ์ พร้อม handle Promise rejection
      const sound = new Audio(isMatch ? trueSound : wrongSound);
      sound.play().catch(() => {});

      setTimeout(() => {
        if (isMatch) {
          // คู่ถูกต้อง
          setCards(prevCards => 
            prevCards.map(card => 
              card.id === card1.id || card.id === card2.id 
                ? { ...card, matched: true } 
                : card
            )
          );
          setMatchedPairs(prev => prev + 1);
        } else {
          // ไม่ตรงกัน - พลิกกลับ
          setCards(prevCards => 
            prevCards.map(card => 
              card.id === card1.id || card.id === card2.id 
                ? { ...card, flipped: false } 
                : card
            )
          );
        }
        
        setFlippedCards([]);
        setCanFlip(true);
      }, 1000);
    }
  }, [flippedCards]);

  // ตรวจสอบเมื่อเกมจบ
  useEffect(() => {
    if (matchedPairs === imagePairs.length) {
      setTimeout(() => {
        setGameWon(true);
      }, 500);
    }
  }, [matchedPairs, imagePairs.length]);

  // จัดการเมื่อคลิกการ์ด
  const handleCardClick = (card) => {
    if (!canFlip || card.flipped || card.matched || flippedCards.length >= 2) {
      return;
    }
    
    // พลิกการ์ด
    setCards(prevCards => 
      prevCards.map(c => 
        c.id === card.id ? { ...c, flipped: true } : c
      )
    );
    
    setFlippedCards(prev => [...prev, card]);
  };

  // เรียงตัวอักษรตามตำแหน่งใน grid (A-T)
  const getLetterForPosition = (index) => {
    const row = Math.floor(index / 5); // แถวที่ 0-3
    const col = index % 5;             // คอลัมน์ที่ 0-4
    return String.fromCharCode(65 + row * 5 + col);
  };

  return (
    <div className="app">
      <div className="header">
        <img 
          src={process.env.PUBLIC_URL + '/Wisdom-Logo.png'} 
          alt="Wisdom Logo" 
          className="header-logo" 
        />
        <h1>เกมจับคู่รูปภาพ</h1>
        <p className="subtitle">จับคู่รูปภาพให้ถูกต้อง!</p>
      </div>
      
      <div className="main-container">
        <div className="game-board">
          {cards.map((card, index) => (
            <div 
              key={card.id}
              className={`card ${card.flipped || card.matched ? 'flipped' : ''} ${card.matched ? 'matched' : ''}`}
              onClick={() => handleCardClick(card)}
            >
              <div className="card-face card-front">
                {getLetterForPosition(index)} {/* แสดงตัวอักษรตามตำแหน่ง */}
              </div>
              <div 
                className="card-face card-back" 
                style={{ backgroundImage: `url(${card.imageUrl})` }}
              ></div>
            </div>
          ))}
        </div>
        
        <div className="game-info">
          <div className="stats-container">
            <div className="stats">
              <div className="stat-box">
                <div className="stat-label">จำนวนครั้งที่พลิก</div>
                <div className="stat-value">{moves}</div>
              </div>
              
              <div className="stat-box">
                <div className="stat-label">คู่ที่ถูกต้อง</div>
                <div className="stat-value">{matchedPairs}/{imagePairs.length}</div>
              </div>
            </div>
            
            <button className="reset-button" onClick={startGame}>
              เริ่มเกมใหม่
            </button>
          </div>
        </div>
      </div>
      
      {gameWon && (
        <div className="win-message show">
          <div className="win-content">
            <h2>ยินดีด้วย!</h2>
            <p>คุณชนะใน {moves} ครั้ง</p>
            <button className="reset-button" onClick={startGame}>เล่นอีกครั้ง</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MatchGame;